import React from 'react';
import { LandingPage, type LandingPageProps } from './LandingPage';
import type { QuizEntryPoint } from '../../../theaWeb/lib/gaPixel';

/**
 * MomsLandingPage — the /welcome2 alias for the working-moms social
 * campaign. Since 2026-07-21 the working-mom experience IS the default
 * homepage (LandingPage's defaults — hero, FeatureStack beats, banner,
 * testimonial order all live there, sourced from homepageStackFrames.ts),
 * so this component no longer defines any content. Its ONLY job is the
 * analytics split: relabel the shared homepage entry points to the moms_*
 * vocabulary so paid-campaign traffic landing on /welcome2 funnels
 * separately from organic homepage (/) traffic in GA4. page_view cohorting
 * needs nothing extra — page_location (/welcome2 vs /) already separates
 * landings.
 *
 * Keep /welcome2 as a live route (not a redirect) precisely so this
 * entry_point relabel survives — a redirect to / would fold the paid funnel
 * back into homepage_*.
 */

/**
 * Relabel the shared homepage entry points to the moms vocabulary so the
 * quiz_start funnel splits per campaign. Exported for direct unit coverage.
 */
export function mapMomsEntryPoint(entryPoint: QuizEntryPoint): QuizEntryPoint {
  switch (entryPoint) {
    case 'homepage_hero':
      return 'moms_hero';
    case 'homepage_banner':
      return 'moms_banner';
    case 'sticky_homepage':
      return 'sticky_moms';
    default:
      return entryPoint;
  }
}

export type MomsLandingPageProps = Omit<
  LandingPageProps,
  'heroSlot' | 'beatsSlot' | 'bannerHeadline' | 'testimonials'
>;

export const MomsLandingPage: React.FC<MomsLandingPageProps> = ({
  onCtaClick,
  ...rest
}) => {
  const handleCtaClick = onCtaClick
    ? (entryPoint: QuizEntryPoint) => onCtaClick(mapMomsEntryPoint(entryPoint))
    : undefined;
  return <LandingPage {...rest} onCtaClick={handleCtaClick} />;
};
